import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ProductSelect } from '../../sections/product-select/product-select';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { CreatorSelectMethod } from '@/app/pages/screening-brief-select/sections/creator-select-method/creator-select-method';
import { ScreeningDefine } from '../../sections/screening-define/screening-define';
import { ScreeningRedFlags } from '../../sections/screening-red-flags/screening-red-flags';
import { ScreeningReportTypeSection } from '../../sections/screening-report-type/screening-report-type';
import { ScreeningReview } from '../../sections/screening-review/screening-review';
import { ScreeningOverlay } from '../../overlays/screening-overlay/screening-overlay';
import { ScreeningPreflightService } from '../../services/screening-preflight.service';
import { PREFLIGHT_STAGES } from '../../screening-brief.constants';

type WizardStepId = 'product' | 'define' | 'redflags' | 'report' | 'review' | 'creators';

interface WizardStep {
  id: WizardStepId;
  /** Rail label. */
  label: string;
  /** Rail sub-label. */
  sub: string;
  /** Short word used in the "Step N · …" eyebrow — not the full rail label. */
  eyeword: string;
  title: string;
  desc: string;
}

interface ChecklistItem {
  label: string;
  ok: boolean;
}

/**
 * The guided Social Screening Brief wizard.
 *
 * Six steps: pick the product, describe what a good fit looks like, build the red-flag list, choose
 * how results come back, review, then add creators. The five "essentials" are guidance, not gates —
 * the pre-flight check on step 5 is the real quality bar, and even that offers a way past.
 */
@Component({
  selector: 'sbs-wizard-first-screening',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductSelect,
    ScreeningDefine,
    ScreeningRedFlags,
    ScreeningReportTypeSection,
    ScreeningReview,
    CreatorSelectMethod,
    ScreeningOverlay,
  ],
  templateUrl: './wizard-first-screening.html',
  styleUrl: './wizard-first-screening.scss',
})
export class WizardFirstScreening {

  /** Services */
  public readonly createScreeningService = inject(CreateScreeningService);
  private readonly preflight = inject(ScreeningPreflightService);
  /** Services - END */

  readonly launch = output<void>();

  readonly preflightStages = PREFLIGHT_STAGES;

  private readonly baseSteps: WizardStep[] = [
    { id: 'product', label: 'Product', sub: "What you're screening for", eyeword: 'Product', title: 'Which product is this for?', desc: 'Pick the product this screening brief protects. It gives the AI context on what a good ambassador looks like.' },
    { id: 'define', label: 'Define the brief', sub: 'Name & requirements', eyeword: 'Define', title: 'What makes a great fit?', desc: "Name the brief and tell the AI the requirements — what you WANT to see in a creator's content. You'll set the red flags in the next step." },
    { id: 'redflags', label: 'Red flags', sub: "What's not allowed", eyeword: 'Red flags', title: 'What raises a red flag?', desc: 'Define the content that would disqualify a creator from the ambassador programme.' },
    { id: 'report', label: 'Reporting type', sub: 'How results come back', eyeword: 'Reporting', title: 'How should we report back?', desc: 'Choose how the screening results are presented. Take the standard report, or write your own spec for what you want to see.' },
    { id: 'review', label: 'Review & check', sub: 'Server pre-flight', eyeword: 'Review', title: "Let's check your brief", desc: "Give it a final look, then we'll validate it before you add creators." },
    { id: 'creators', label: 'Add creators', sub: 'Who to screen', eyeword: 'Creators', title: 'Who should we screen?', desc: 'Add the creators you want to run this screening brief against — four ways, your pick.' },
  ];

  readonly steps = computed(() => {
    const usePreselected = this.createScreeningService.usePreselectedCreators();
    return this.baseSteps.map((step) => {
      const creatorsCopy = usePreselected
        ? {
            title: 'Confirm who to screen',
            desc: 'These creators will be checked against your screening rules when you launch.',
            sub: 'Ready to screen',
          }
        : {};
      return { ...step, ...(step.id === 'creators' ? creatorsCopy : {}) };
    });
  });

  readonly current = signal<number>(0);
  readonly step = computed(() => this.steps()[this.current()]);
  readonly eyebrow = computed(() => `Step ${this.current() + 1} · ${this.step().eyeword}`);

  readonly progressPct = computed(() => (this.current() / (this.steps().length - 1)) * 100);

  /** The five essentials. Advisory — they drive the footer label, they do not block navigation. */
  readonly checklist = computed<ChecklistItem[]>(() => {
    const service = this.createScreeningService;
    const reportType = service.reportType();
    return [
      { label: 'Product', ok: !!service.selectedProduct() },
      { label: 'Name', ok: service.basicInfoModel().briefTitle.trim().length > 0 },
      { label: 'What to look for', ok: service.lookFor().trim().length >= 20 },
      { label: 'At least one red flag', ok: service.restrictions().length >= 1 },
      {
        label: 'Reporting type',
        ok:
          reportType === 'standard' ||
          (reportType === 'custom' && service.reportPrompt().trim().length >= 20),
      },
    ];
  });

  readonly essentialsDone = computed(() => this.checklist().filter((item) => item.ok).length);
  readonly essentialsComplete = computed(() => this.checklist().every((item) => item.ok));

  readonly primaryLabel = computed(() => {
    switch (this.step().id) {
      case 'review':
        return 'Run pre-flight check';
      case 'creators':
        return 'Submit brief';
      default:
        return 'Continue';
    }
  });

  readonly briefName = computed(
    () => this.createScreeningService.basicInfoModel().briefTitle.trim() || 'your brief',
  );

  /** Pre-flight overlay state. */
  readonly checkOpen = signal<boolean>(false);
  readonly checkResult = signal<'running' | 'pass' | 'fail'>('running');
  readonly checkReasons = signal<readonly string[]>([]);

  readonly checkResultTitle = computed(() =>
    this.checkResult() === 'pass' ? 'This Looks Solid.' : 'Your Brief Looks Thin.',
  );

  readonly checkResultSub = computed(() => {
    if (this.checkResult() === 'pass') {
      return "Your brief has clear requirements and restrictions. You're ready to add the creators you want to screen.";
    }
    return this.essentialsComplete()
      ? "Everything required is filled in, so you can carry on — but a thin brief misses more than it catches. Here's what we'd tighten:"
      : "We couldn't confirm valid requirements yet. Tighten it up and run the check again:";
  });

  private readonly scrollArea = viewChild<ElementRef<HTMLElement>>('scrollArea');

  /** Whatever was focused when an overlay opened, so focus can go back on close. */
  private returnFocusTo: HTMLElement | undefined;

  constructor() {
    // Rail jumps and overlay-driven jumps both land at the top of the new step.
    effect(() => {
      this.current();
      this.scrollArea()?.nativeElement.scrollTo({ top: 0 });
    });
  }

  isDone(index: number): boolean {
    return index < this.current();
  }

  stepCircleClass(index: number): string {
    if (index === this.current()) {
      return 'border-brand-studio bg-brand-studio text-white';
    }
    if (this.isDone(index)) {
      return 'border-brand-studio bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio';
    }
    return 'border-border text-muted-foreground';
  }

  goTo(index: number): void {
    this.current.set(index);
  }

  back(): void {
    if (this.current() > 0) {
      this.current.update((c) => c - 1);
    }
  }

  next(): void {
    if (this.current() < this.steps().length - 1) {
      this.current.update((c) => c + 1);
    }
  }

  /** The footer's primary action, which changes meaning on the last two steps. */
  primary(): void {
    switch (this.step().id) {
      case 'review':
        this.runCheck();
        break;
      case 'creators':
        this.launch.emit();
        break;
      default:
        this.next();
    }
  }

  runCheck(): void {
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.checkResult.set('running');
    this.checkReasons.set([]);
    this.checkOpen.set(true);
  }

  /** Fires once the overlay has walked its stage list. */
  onCheckSequenceComplete(): void {
    const result = this.preflight.run();
    this.checkReasons.set(result.reasons);
    if (result.pass) {
      this.createScreeningService.checkPassed.set(true);
    }
    this.checkResult.set(result.pass ? 'pass' : 'fail');
  }

  closeCheck(): void {
    this.checkOpen.set(false);
    this.returnFocusTo?.focus();
    this.returnFocusTo = undefined;
  }

  closeCheckAndGoTo(id: WizardStepId): void {
    this.closeCheck();
    this.goTo(this.baseSteps.findIndex((step) => step.id === id));
  }

  /** Available only once every essential is satisfied — a thin brief is then the user's call. */
  continueAnyway(): void {
    this.createScreeningService.checkPassed.set(true);
    this.closeCheckAndGoTo('creators');
  }
}
